// Référentiels du formulaire partenaires et de la fiche, repris du prototype v3 validé
// par David le 02/10/2026 (https://claude.ai/artifact/67LbaAhUhS54nLkHMD6d5k).
//
// Textes en français seulement : l'espace partenaires s'adresse aux acteurs français
// (vouvoiement), il n'a pas de version anglaise.

import { frCoreMessages } from '@/lib/messages/fr/core';

import type { IconName } from './icons';
import type {
  Activity,
  Arrival,
  ConditionCode,
  Delay,
  PaidIn,
  PayMean,
  PriceGiven,
  RangeCode,
  RuleSense,
  Unsold,
} from './types';

export type Domain = { code: string; name: string; icon: IconName; types: Array<[string, string]> };

// Les 18 domaines du moteur et leurs sous-catégories, avec les noms du formulaire :
// « Jeu vidéo » rangé aussi dans Électronique, avec le même code moteur que dans Jeux et jouets.
export const DOMAINS: Domain[] = [
  { code: 'electronics', name: 'Électronique', icon: 'smartphone', types: [['smartphone', 'Smartphone'], ['feature-phone', 'Téléphone classique'], ['tablet', 'Tablette'], ['e-reader', 'Liseuse'], ['smartwatch', 'Montre connectée'], ['headphones-audio', 'Casques et audio'], ['camera', 'Appareil photo'], ['television', 'Téléviseur'], ['gaming-console', 'Console de jeu'], ['video-game', 'Jeu vidéo'], ['mobile-accessory', 'Accessoire mobile'], ['smart-home-device', 'Objet connecté pour la maison'], ['portable-media-player', 'Baladeur'], ['drone', 'Drone']] },
  { code: 'computers', name: 'Informatique', icon: 'laptop', types: [['laptop', 'Ordinateur portable'], ['desktop-computer', 'Ordinateur de bureau'], ['monitor', 'Moniteur'], ['pc-component', 'Composant PC'], ['networking-gear', 'Équipement réseau'], ['printer-scanner', 'Imprimante et scanner'], ['computer-accessory', 'Accessoire informatique']] },
  { code: 'home-appliances', name: 'Électroménager', icon: 'plug', types: [['small-kitchen-appliance', 'Petit appareil de cuisine'], ['large-kitchen-appliance', 'Gros électroménager'], ['laundry-appliance', 'Appareil de lavage'], ['cleaning-appliance', 'Appareil de nettoyage'], ['climate-control-appliance', 'Appareil de climatisation'], ['personal-care-appliance', 'Appareil de soin personnel']] },
  { code: 'furniture', name: 'Meubles', icon: 'sofa', types: [['sofa', 'Canapé'], ['table', 'Table'], ['chair', 'Chaise'], ['storage-furniture', 'Meuble de rangement'], ['bedroom-furniture', 'Meuble de chambre'], ['mattress', 'Matelas'], ['office-furniture', 'Mobilier de bureau'], ['outdoor-furniture', 'Mobilier d’extérieur'], ['seating-misc', 'Banc et pouf'], ['media-furniture', 'Meuble TV']] },
  { code: 'home-decor', name: 'Maison et décoration', icon: 'home', types: [['lighting', 'Éclairage'], ['rug-textile', 'Tapis et textile'], ['wall-art', 'Art mural'], ['kitchenware', 'Ustensiles de cuisine'], ['garden-decor', 'Décoration de jardin'], ['decorative-object', 'Objet de décoration']] },
  { code: 'fashion', name: 'Mode', icon: 'shirt', types: [['womens-clothing', 'Vêtements femme'], ['mens-clothing', 'Vêtements homme'], ['footwear', 'Chaussures'], ['bag', 'Sac'], ['watch-jewelry', 'Montres et bijoux'], ['fashion-accessory', 'Accessoire de mode']] },
  { code: 'baby-kids', name: 'Bébé et enfants', icon: 'blocks', types: [['stroller', 'Poussette'], ['car-seat', 'Siège auto'], ['baby-gear', 'Équipement bébé'], ['kids-clothing', 'Vêtements enfant'], ['learning-toy', 'Jouet éducatif'], ['maternity-item', 'Article de maternité'], ['child-furniture', 'Mobilier enfant']] },
  { code: 'sports-outdoors', name: 'Sports et plein air', icon: 'dumbbell', types: [['fitness-gear', 'Équipement de fitness'], ['bicycle', 'Vélo'], ['bicycle-accessory', 'Accessoire vélo'], ['e-scooter-mobility', 'Trottinette et mobilité'], ['running-gear', 'Équipement de course'], ['team-sport-gear', 'Équipement de sport collectif'], ['camping-hiking-gear', 'Équipement de camping et randonnée'], ['water-sport-gear', 'Équipement de sport nautique'], ['racket-sport-gear', 'Sport de raquette'], ['winter-sport-gear', 'Sport d’hiver'], ['fishing-gear', 'Pêche'], ['equestrian-gear', 'Équitation'], ['golf-gear', 'Golf'], ['skate-gear', 'Skate et roller']] },
  { code: 'tools-diy', name: 'Outils et bricolage', icon: 'wrench', types: [['power-tool', 'Outil électrique'], ['hand-tool', 'Outil manuel'], ['workshop-equipment', 'Équipement d’atelier'], ['construction-material', 'Matériau de construction'], ['gardening-tool', 'Outil de jardinage'], ['painting-decorating-tool', 'Outil de peinture']] },
  { code: 'auto-moto', name: 'Auto et moto', icon: 'car', types: [['car-part', 'Pièce auto'], ['moto-part', 'Pièce moto'], ['wheel-tire', 'Roue et pneu'], ['car-electronic', 'Équipement électronique auto'], ['vehicle-accessory', 'Accessoire véhicule'], ['car-battery-electrical', 'Batterie et électricité auto']] },
  { code: 'books-media', name: 'Livres et médias', icon: 'book', types: [['book', 'Livre'], ['comic-magazine', 'Bande dessinée et magazine'], ['movie-disc', 'DVD et Blu-ray'], ['music-recording', 'CD et vinyles']] },
  { code: 'games-toys', name: 'Jeux et jouets', icon: 'gamepad', types: [['video-game', 'Jeu vidéo'], ['board-game', 'Jeu de société'], ['puzzle', 'Puzzle'], ['construction-set', 'Jeu de construction'], ['action-figure', 'Figurine'], ['educational-toy', 'Jouet éducatif'], ['electronic-toy', 'Jouet électronique'], ['outdoor-toy', 'Jouet d’extérieur'], ['model-vehicle-toy', 'Véhicule miniature'], ['hobby-model', 'Maquette et modélisme'], ['game-room-equipment', 'Billard, baby-foot et arcade']] },
  { code: 'collectibles-art', name: 'Collections et art', icon: 'gem', types: [['coin-stamp', 'Pièce et timbre'], ['trading-card', 'Carte à collectionner'], ['artwork', 'Œuvre d’art'], ['vintage-item', 'Objet vintage']] },
  { code: 'musical-instruments', name: 'Instruments de musique', icon: 'music', types: [['guitar-bass', 'Guitare et basse'], ['keyboard-piano', 'Clavier et piano'], ['drum-percussion', 'Batterie et percussion'], ['dj-audio-gear', 'Matériel DJ et audio'], ['studio-recording-gear', 'Matériel de studio'], ['string-instrument', 'Instrument à cordes'], ['wind-brass-instrument', 'Instrument à vent']] },
  { code: 'health-beauty', name: 'Santé et beauté', icon: 'sparkles', types: [['skincare-item', 'Produit de soin'], ['makeup-item', 'Produit de maquillage'], ['haircare-item', 'Produit capillaire'], ['wellness-device', 'Appareil de bien-être'], ['medical-equipment', 'Équipement médical'], ['eyewear', 'Lunettes'], ['fragrance', 'Parfum']] },
  { code: 'pets', name: 'Animaux', icon: 'paw', types: [['pet-supplies', 'Accessoires pour animaux'], ['pet-habitat', 'Cage, niche et panier']] },
  { code: 'plants', name: 'Plantes', icon: 'leaf', types: [['live-plant', 'Plante vivante'], ['plant-care-supplies', 'Accessoires pour plantes']] },
  { code: 'office-school', name: 'Bureau et école', icon: 'briefcase', types: [['stationery', 'Papeterie'], ['school-supplies', 'Fournitures scolaires'], ['office-equipment', 'Équipement de bureau'], ['office-supplies', 'Fournitures de bureau']] },
];

export const DOMAIN_BY_CODE: Record<string, Domain> = Object.fromEntries(DOMAINS.map((domain) => [domain.code, domain]));

// Noms des sous-types (« Vélo électrique ») : ceux de l'app, complétés ici pour les
// sous-types que l'app n'affiche pas encore en français.
const TAXONOMY_LABELS = frCoreMessages.taxonomy.labels as Record<string, string>;
const SUBTYPE_NAMES: Record<string, string> = {
  sportwatch: 'Montre de sport', 'fitness-tracker': 'Bracelet d’activité', 'amplifier-receiver': 'Amplificateur',
  'hifi-separate': 'Élément hi-fi', 'mp3-player': 'Lecteur MP3', 'portable-radio': 'Radio portable',
  'boombox-stereo': 'Chaîne et radio-cassette', 'camera-drone': 'Drone avec caméra', 'toy-drone': 'Drone jouet',
  bench: 'Banc', 'ottoman-pouf': 'Pouf', 'bean-bag': 'Pouf poire', 'tv-stand': 'Meuble TV', 'media-unit': 'Meuble multimédia',
  'tablecloth-table-linen': 'Linge de table', 'throw-pillow-cover': 'Coussin et housse', 'photo-frame': 'Cadre photo',
  'tapestry-wall-hanging': 'Tapisserie', vase: 'Vase', 'candle-holder': 'Bougie et bougeoir', clock: 'Horloge',
  'figurine-ornament': 'Bibelot', 'decorative-bowl-tray': 'Coupe et plateau', 'basket-box': 'Panier et boîte',
  'underwear-lingerie': 'Lingerie', swimwear: 'Maillot de bain', sleepwear: 'Pyjama', 'underwear-socks': 'Sous-vêtement et chaussettes',
  'baby-learning-toy': 'Jouet d’éveil', 'kids-bike': 'Vélo enfant et draisienne', 'hiking-backpack': 'Sac de randonnée',
  'climbing-gear': 'Matériel d’escalade', 'golf-clubs': 'Club de golf', 'golf-bag-cart': 'Sac et chariot de golf',
  'golf-accessory': 'Accessoire de golf', skateboard: 'Skateboard', 'roller-inline-skates': 'Rollers', 'protective-pads': 'Protections',
  'chainsaw-brushcutter': 'Tronçonneuse et débroussailleuse', 'paint-roller-brush': 'Rouleau et pinceau',
  'paint-sprayer': 'Pistolet à peinture', 'scraper-filling-tool': 'Grattoir et couteau à enduire', 'engine-part': 'Pièce moteur',
  'braking-part': 'Pièce de freinage', 'suspension-steering': 'Suspension et direction', 'exhaust-part': 'Pièce d’échappement',
  'transmission-part': 'Pièce de transmission', 'starter-battery': 'Batterie de démarrage', 'alternator-starter': 'Alternateur et démarreur',
  'card-game': 'Jeu de cartes', 'dice-tile-game': 'Jeu de dés et de tuiles', 'jigsaw-puzzle': 'Puzzle', 'brain-teaser': 'Casse-tête',
  'diecast-model': 'Modèle réduit', 'toy-train-set': 'Train miniature', 'slot-car-set': 'Circuit de voitures',
  'arcade-cabinet': 'Borne d’arcade', pinball: 'Flipper', jukebox: 'Juke-box', 'foosball-table': 'Baby-foot',
  'pool-billiard-table': 'Billard', 'air-hockey': 'Air hockey', dartboard: 'Cible de fléchettes', synthesizer: 'Synthétiseur',
  'midi-controller': 'Contrôleur MIDI', 'electronic-drums': 'Batterie électronique', viola: 'Alto', cello: 'Violoncelle',
  'other-string': 'Autre instrument à cordes', woodwind: 'Bois', brass: 'Cuivre', harmonica: 'Harmonica',
  'prescription-glasses': 'Lunettes de vue', 'reading-glasses': 'Lunettes de lecture', frames: 'Monture',
  'sealed-fragrance': 'Parfum scellé', 'cage-hutch': 'Cage et clapier', 'cat-tree': 'Arbre à chat', 'crate-kennel': 'Caisse et niche',
  'aquarium-terrarium': 'Aquarium et terrarium', 'seeds-bulbs': 'Graines et bulbes', 'school-backpack': 'Cartable',
};
export function subtypeName(code: string): string {
  return TAXONOMY_LABELS[code] || SUBTYPE_NAMES[code] || code;
}

export const ACTIVITIES: Array<[Activity, string]> = [
  ['rachat', 'Rachat'],
  ['depot_vente', 'Dépôt-vente'],
  ['vente_pour_compte', 'Vente pour le compte du vendeur'],
  ['plateforme', 'Plateforme de vente entre particuliers'],
  ['don', 'Don'],
  ['recyclage', 'Recyclage'],
  ['reparation', 'Réparation'],
];
export const CONDITION_CODES: ConditionCode[] = ['comme_neuf', 'bon', 'moyen', 'use', 'casse'];
export const CONDITION_NAMES = ['Comme neuf', 'Bon', 'Moyen', 'Usé', 'Cassé'];
export const RANGE_CODES: RangeCode[] = ['premiers_prix', 'milieu', 'premium', 'luxe'];
export const RANGE_NAMES = ['Premiers prix', 'Milieu de gamme', 'Premium', 'Luxe'];
export const COLLECTIBLES: Array<['specialite' | 'accepte' | 'refuse', string]> = [
  ['specialite', 'C’est notre spécialité'],
  ['accepte', 'On les prend comme le reste'],
  ['refuse', 'On n’en prend pas'],
];
export const MIN_VALUES: Array<[string, string]> = [
  ['aucune', 'Aucune'],
  ['5', '5 €'],
  ['10', '10 €'],
  ['20', '20 €'],
  ['50', '50 €'],
  ['autre', 'Autre'],
];
// Conditions proposées selon les catégories cochées (prototype v3).
export const SUGGESTED_RULES: Record<string, Array<[string, RuleSense, string]>> = {
  _all: [
    ['propre', 'ok', 'Propre et sans défaut'],
    ['complet', 'ok', 'Complet, avec ses accessoires'],
    ['marche', 'ok', 'En état de marche'],
  ],
  _rachat: [['identite', 'ok', 'Être majeur, avec une pièce d’identité']],
  electronics: [
    ['reinit', 'ok', 'Réinitialisé, comptes déconnectés'],
    ['chargeur', 'ok', 'Avec son chargeur'],
  ],
  computers: [
    ['reinit', 'ok', 'Réinitialisé, comptes déconnectés'],
    ['chargeur', 'ok', 'Avec son chargeur'],
  ],
  fashion: [
    ['bon_marche', 'non', 'Pas de marques bon marché (Kiabi, Shein, Primark…)'],
    ['sous_vet', 'non', 'Pas de sous-vêtements déjà portés'],
  ],
  'baby-kids': [['normes', 'ok', 'Conforme aux normes de sécurité actuelles']],
  furniture: [['demonte', 'info', 'Meubles démontés ou démontables']],
};
export const ARRIVALS: Array<[Arrival, string, IconName]> = [
  ['magasin', 'En magasin', 'store'],
  ['colis', 'Par colis', 'package'],
  ['collecte', 'Vous venez le chercher', 'truck'],
  ['point_relais', 'En point relais', 'pin'],
];
export const PRICE_GIVEN: Array<[PriceGiven, string]> = [
  ['site', 'En ligne, sur votre site'],
  ['liberio', 'Sur photos, via Liberio'],
  ['sur_place', 'Sur place ou à réception'],
];
export const PAID_IN: Array<[PaidIn, string]> = [
  ['argent', 'Argent'],
  ['bon_achat', 'Bon d’achat'],
];
export const PAY_MEANS: Array<[PayMean, string]> = [
  ['virement', 'Virement'],
  ['especes', 'Espèces'],
  ['cheque', 'Chèque'],
];
export const DELAYS: Array<[Delay, string]> = [
  ['1_2_semaines', '1 à 2 semaines'],
  ['2_4_semaines', '2 à 4 semaines'],
  ['5_10_semaines', '5 à 10 semaines'],
  ['plus_10_semaines', 'Plus de 10 semaines'],
];
export const DELAY_TEXT: Record<Delay, string> = {
  '1_2_semaines': '1 à 2 semaines',
  '2_4_semaines': '2 à 4 semaines',
  '5_10_semaines': '5 à 10 semaines',
  plus_10_semaines: 'plus de 10 semaines',
};
export const UNSOLD: Array<[Unsold, string]> = [
  ['rendu_gratuit', 'Rendu gratuitement'],
  ['rendu_frais', 'Rendu contre des frais'],
  ['donne', 'Donné ou recyclé'],
];
export const SCOPES: Array<['magasin' | 'enseigne' | 'partie', string]> = [
  ['magasin', 'Ce magasin seulement'],
  ['enseigne', 'Tous nos magasins'],
  ['partie', 'Une partie de nos magasins'],
];

// Textes des « i » : la définition du mot, chaque terme en gras (règle du 02/10).
export const INFO: Record<string, Array<[string, string]>> = {
  etats: [
    ['Comme neuf', ' : jamais ou très peu utilisé, sans trace. '],
    ['Bon', ' : quelques traces légères. '],
    ['Moyen', ' : traces visibles, fonctionne bien. '],
    ['Usé', ' : très marqué, mais utilisable. '],
    ['Cassé', ' : ne fonctionne plus.'],
  ],
  rares: [['', 'Pièces anciennes, éditions rares, séries limitées : des objets qui valent plus pour un collectionneur.']],
  valeurMin: [
    ['Par objet', ' : chaque objet doit valoir au moins ce montant à la revente. '],
    ['Par lot', ' : c’est l’ensemble déposé ou envoyé en une fois qui compte.'],
  ],
  email: [
    ['Affiché', ' : les vendeurs voient votre adresse sur votre fiche. '],
    ['Privé', ' : ils vous écrivent depuis Liberio, sans voir l’adresse.'],
  ],
  prix: [['Sur photos, via Liberio', ' : les vendeurs vous envoient des photos de leur objet depuis l’app, et vous répondez avec un prix.']],
  mode: [
    ['À chaque tranche', ' : avec 40 % de 0 à 50 € puis 70 % au-delà, un objet vendu 100 € rapporte 20 € + 35 € = 55 €. '],
    ['Au prix entier', ' : le pourcentage de la tranche atteinte s’applique à tout le prix ; avec 40 % de 0 à 100 €, un objet vendu 100 € rapporte 40 €.'],
  ],
  atouts: [['', 'Ce qui vous distingue, en quelques mots : années d’expérience, nombre d’experts, garanties… Nous nous en servons pour expliquer aux vendeurs pourquoi nous vous recommandons.']],
  avantage: [['', 'Un avantage réservé aux vendeurs qui viennent de Liberio. Par exemple : un code qui donne 10 % de bonus sur la reprise. Il s’affiche sur votre fiche.']],
};
